import { Loader2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";

export function Spinner({ className = "" }) {
  return <Loader2 className={`animate-spin ${className}`} />;
}

// Ticking countdown seeded from a server-provided seconds value.
export function Countdown({ seconds, onDone, testId, className = "" }) {
  const [left, setLeft] = useState(seconds ?? 0);
  const done = useRef(false);
  useEffect(() => { setLeft(seconds ?? 0); done.current = false; }, [seconds]);
  useEffect(() => {
    const t = setInterval(() => setLeft((s) => {
      if (s <= 1) { if (!done.current) { done.current = true; onDone && onDone(); } return 0; }
      return s - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [onDone]);
  const h = Math.floor(left / 3600), m = Math.floor((left % 3600) / 60), s = left % 60;
  const txt = h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
  return <span data-testid={testId} className={`tabular-nums mono ${className}`}>{txt}</span>;
}

export function PageLoader({ label = "Booting systems" }) {
  return (
    <div className="min-h-[60vh] grid place-items-center" data-testid="page-loader">
      <div className="flex flex-col items-center gap-4 text-muted-foreground">
        <Spinner className="w-8 h-8 text-primary" />
        <span className="label-cap">{label}</span>
      </div>
    </div>
  );
}

const STAT_COLORS = {
  hunger: "hsl(var(--secondary))",
  energy: "hsl(var(--primary))",
  happiness: "hsl(var(--accent))",
};

export function StatBar({ label, value, kind, icon: Icon, testId }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="label-cap flex items-center gap-1.5">
          {Icon && <Icon className="w-3.5 h-3.5" />} {label}
        </span>
        <span className="text-sm font-bold tabular-nums">{pct}</span>
      </div>
      <div className="h-3 rounded-full bg-muted overflow-hidden dark:rounded-none">
        <div
          className="h-full rounded-full dark:rounded-none transition-[width] duration-500"
          style={{ width: `${pct}%`, background: STAT_COLORS[kind] || "hsl(var(--primary))" }}
        />
      </div>
    </div>
  );
}

export function Modal({ open, onClose, children, testId }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" data-testid={testId}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg panel-glow bg-card p-6 pop-in">{children}</div>
    </div>
  );
}
